/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { TypescriptComponent } from 'components/typescriptExample'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Timeline } from '../timeline'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import MapSelector from 'components/MapSelector'
import { Image, Link } from '@ta-interaktiv/contentbricks'
import { browserCheck } from '@ta-interaktiv/browsercheck'

// TODO Set the correct article ID here
const ARTICLE_ID = '209286268543'

// const MapContainer = styled.div`
//   margin: auto;
//   max-width: 900px;
//   @media (max-width: 900px) {
//     margin: auto 15px;
//   }
// `

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical very fitted segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>Was Sie über unser Agrarwesen wissen müssen</h1>
            <h2>
              Am 13. Juni stimmen wir über zwei Vorlagen ab, die das Schweizer
              Agrarwesen radikal umbauen wollen: Direktzahlungen an Bauern
              sollen neu gesteuert, der Pestizideinsatz stärker reguliert, die
              Produktion ökologischer werden. Eine grosse Übersicht zeigt, wie
              unsere Landwirtschaft aktuell ausgerichtet ist.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              Salome&nbsp;Müller, Hans&nbsp;Brandt, Patrick&nbsp;Meier,
              Patrick&nbsp;Vögeli
            </div>
            <div className='ta updated'>Aktualisiert am 24. Mai 2021</div>
          </div>
          <div className='ta text'>
            <p>
              Zwei Agrarinitiativen wollen eine umweltfreundlichere
              Landwirtschaft in der Schweiz erreichen. Die{' '}
              <Link href='771333081510'>Trinkwasserinitiative</Link> sieht dafür
              eine Steuerung über Direktzahlungen an die Bauern vor, die{' '}
              <Link href='855522131867'>Pestizidinitiative</Link> verlangt ein
              Verbot von synthetischen Pestiziden.
            </p>
            <p>
              Beide Initiativen polarisieren, weil sie das Schweizer Agrarwesen
              radikal umbauen würden. Aber worüber sprechen wir eigentlich, wenn
              wir von unserer Landwirtschaft reden?
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <h4 className='chapter'>
                <b>1</b>
                <br />
                Betriebe und Nutztiere
              </h4>{' '}
              <p>
                Die Zahl der Landwirtschaftsbetriebe in der Schweiz geht seit
                Jahrzehnten zurück. 2020 gab es noch 49’363 Betriebe – 675 Höfe
                weniger als im Jahr zuvor. Gleichzeitig werden die Betriebe
                grösser. Ein Hof bewirtschaftet im Durchschnitt 21 Hektaren.
              </p>
              <p>
                2019 war die landwirtschaftliche Nutzfläche insgesamt 1 Million
                Hektaren gross, 70 Prozent davon sind Grünflächen, etwa Wiesen
                und Weiden.
              </p>
              <p>
                In den vergangenen 45 Jahren hat sich auch der Bestand der
                Nutztiere verändert. Die Anzahl der Hühner hat sich praktisch
                verdoppelt: Die Nachfrage nach Poulet und Eiern ist stark
                gestiegen. Gleichzeitig ging der Bestand von Schweinen, Rindern
                und Kühen zurück. 2020 erreichte der Kuhbestand mit 680’000
                Tieren den tiefsten Wert seit 1996.
              </p>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui container'>
            <div className='ui two column grid doubling stackable'>
              <div className='row'>
                <div className='column special'>
                  <div className='titleBox'>
                    <h4 className='graphicTitle'>
                      Entwicklung der Nutztierhaltung
                    </h4>
                    <div className='graphicSub'>
                      <div>In Prozent, seit 1975</div>
                      <div style={{ marginTop: '0.5rem' }}>
                        <span className='bold'>Lesebeispiel:</span> 2020 gab es
                        fast doppelt so viele Hühner in der Schweiz wie im
                        Referenzjahr 1975.
                      </div>
                    </div>
                  </div>
                  <div className='graphicBox'>
                    <div className='boxWrapper'>
                      <div className='iframeBox'>
                        <AutofitIFrame
                          src='//datawrapper.dwcdn.net/GdhWl/'
                          style={{ height: '100%' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='column special'>
                  <div className='titleBox'>
                    <h4 className='graphicTitle'>Nutztiere</h4>
                    <div className='graphicSub'>2020, Tiere in Tausenden</div>
                  </div>

                  <div className='graphicBox donut'>
                    <AutofitIFrame
                      src='//datawrapper.dwcdn.net/NdoYt/'
                      // style={{ height: '100%' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <p>
                Im Jahr 2020 gab es in der Schweiz insgesamt ca. 15 Millionen
                Nutztiere. Hühner machen mit 78,1 Prozent den grössten Anteil
                aus. Die Geflügelhaltung erlebte zwischen 2006 und 2019 einen
                regelrechten{' '}
                <Link href='425309058798'>
                  Boom
                </Link>
                : Die Zahl der Mastpoulets nahm noch stärker zu als die der
                Legehennen.
              </p>
            </div>
          </div>
        </div>

        {/* <div className='ui vertical fitted segment'>
        <div className='ui text container'>
          <div className='column special'>
            <div className='titleBox'>
              <h4 className='graphicTitle'>Bio Anteil</h4>
              <div className='graphicSub'>
                Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed
                diam nonumy eirmod tempor invidunt ut labore et dolore magna
                aliquyam erat, sed diam voluptua.
              </div>
            </div>
            <div className='graphicBox'>
              <div className='boxWrapper'>
                <div className='iframeBox'>
                  <AutofitIFrame
                    src='//datawrapper.dwcdn.net/JLY0a/'
                    style={{ height: '100%' }}
                  />
                </div>
              </div>
              <div className='source'>
                Hinweis: Anteil der Tiere, die auf einem Betrieb leben bei dem
                ein Teil oder die Gesamtheit seiner landwirtschaftlichen
                Nutzfläche biozertifiziert ist. Vor 1990 sind die Bio-Anteile
                nicht erfasst.
              </div>
            </div>
          </div>
        </div>
      </div> */}
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <h4 className='chapter'>
                <b>2</b>
                <br />
                Futtermittel
              </h4>
              <p>
                Ein zentrales Anliegen der Trinkwasserinitiative ist die
                Futtermittelproduktion. Nur noch jene Landwirtschaftsbetriebe
                sollen Subventionen erhalten, die ihre Tiere mit eigenem Futter
                ernähren können.
              </p>
              <p>
                Rindvieh, Schafe und Ziegen leben vorwiegend von Raufutter. Es stammt von
                Wiesen und Weiden und wird fast ausschliesslich in der Schweiz
                produziert.
              </p>
              <p>
                Schweine und Hühner essen Kraftfutter wie Weizen oder Soja, das
                einen höheren Proteinanteil hat; mehr als zwei Drittel davon
                stammen aus dem Ausland.
              </p>
              <p>
                Seit 1990 hat sich der Import von Futtermittel fast verfünffacht
                und betrug 2018 nach Proteingehalt berechnet 293’000 Tonnen. Das
                im Inland hergestellte Futter ging von 1’045’000 Tonnen 1990 auf
                873’000 Tonnen (ebenfalls nach Proteingehalt berechnet) 2018
                zurück – eine Abnahme von 16 Prozent.
              </p>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className=''>
              <div className='titleBox noBorder'>
                <h4 className='graphicTitle'>Herkunft der Futtermittel</h4>
                <div className='graphicSub'>
                  <div>In 1000 Tonnen (Proteingehalt)</div>
                  <div style={{ marginTop: '0.5rem' }}>
                    <span className='bold'>Lesebeispiel:</span> Im Jahr 2018
                    wurden 293'000 Tonnen Futtermittel importiert
                    (Proteinanteil). Dies entspricht rund 25 Prozent des
                    gesamten Futtermittelbedarfs.
                  </div>
                </div>
              </div>
              <div className=''>
                <div className=''>
                  <div className=''>
                    <AutofitIFrame src='//datawrapper.dwcdn.net/dTfs2/' />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <p>
                Auch Milchkühe erhalten teilweise Kraftfutter. Aber vom
                importierten Futter abhängig ist vor allem die intensive
                Schweine- und Hühnerproduktion, die seit Jahren boomt. Ohne
                Importfutter würde die Menge an Schweizer Pouletfleisch und
                Eiern drastisch sinken, jene von Schweinefleisch wäre stark
                betroffen.
              </p>
            </div>
          </div>
        </div>

        <div className='ui vertical fitted segment'>
          <div className='ui container'>
            <h4 className='graphicTitle'>Futteranteil Inland und Import</h4>
            <div className='graphicSub'>
              Dreijahresmittel 2016-2018, Proteingehalt im Futter, in Tonnen pro
              Jahr
            </div>
            <div className='graphicLegend'>
              <div className='itemWrapper'>
                <div className='square red'></div>
                <div className='legendItem'>Importanteil</div>
              </div>
              <div className='itemWrapper'>
                <div className='square beige'></div>
                <div className='legendItem'>Inlandanteil</div>
              </div>
            </div>
            <div className='ui three column grid doubling stackable'>
              <div className='row animal'>
                <div className='column special animal'>
                  <div className='titleBox animal'>
                    <div>
                      <h4 className='graphicTitle animal'>Rindvieh</h4>
                      <div className='graphicSub animal'>587’230 t</div>
                    </div>

                    <img
                      className='ui image left'
                      src={require('../../images/rindvieh.svg')}
                    />
                  </div>
                  <div className='pieChartContainer'>
                    <AutofitIFrame src='//datawrapper.dwcdn.net/okEks/' />
                  </div>
                </div>

                <div className='column special animal'>
                  <div className='titleBox animal'>
                    <div>
                      <h4 className='graphicTitle animal'>Schweine</h4>
                      <div className='graphicSub animal'>133’919 t</div>
                    </div>

                    <img
                      className='ui image left'
                      src={require('../../images/schwein.svg')}
                    />
                  </div>
                  <div className='pieChartContainer'>
                    <AutofitIFrame src='//datawrapper.dwcdn.net/eyQke/' />
                  </div>
                </div>

                <div className='column special animal'>
                  <div className='titleBox animal'>
                    <div>
                      <h4 className='graphicTitle animal'>Hühner</h4>
                      <div className='graphicSub animal'>75’635 t</div>
                    </div>

                    <img
                      className='ui image left'
                      src={require('../../images/gefluegel.svg')}
                    />
                  </div>
                  <div className='pieChartContainer'>
                    <AutofitIFrame src='//datawrapper.dwcdn.net/RJekQ/' />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className=''>
              <div className='titleBox noBorder'>
                <h4 className='graphicTitle'>
                  Fleischproduktion aus Inland- bzw. Importfutter
                </h4>
                <div className='graphicSub'>
                  Nutzbares Fleisch (ohne Knochen, Nettogewicht), in Tonnen pro
                  Jahr
                </div>
              </div>
              <div className=''>
                <div className=''>
                  <div className=''>
                    <AutofitIFrame src='//datawrapper.dwcdn.net/nu3pa/' />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <p>
                Importiertes Futter erlaubt es, die Zahl der landwirtschaftlich
                genutzten Tiere in der Schweiz stark zu steigern. Das wiederum
                verursacht mehr Gülle und trägt zur Belastung des Grundwassers
                bei.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <h4 className='chapter'>
                <b>3</b>
                <br />
                Pestizide
              </h4>
              <p>
                Die Trinkwasserinitiative und die Pestizidinitiative sehen ein
                Verbot von Pestiziden vor. Die Pestizidinitiative geht weiter.
                Sie will einerseits synthetische Pestizide in der Landwirtschaft
                verbieten und andererseits importierte Lebensmittel, die unter
                Einsatz von synthetischen Pestiziden produziert wurden.
              </p>
              <p>
                Als Pestizide bezeichnet man Substanzen, die eingesetzt werden,
                um als schädlich betrachtete Lebewesen zu töten oder deren
                Keimung, Wachstum oder Vermehrung zu hemmen.
              </p>
              <p>
                Pestizide kann man im Wesentlichen in folgende Kategorien
                einteilen:
              </p>
              <ul>
                <li>Herbizide (Unkrautgifte)</li>
                <li>Fungizide (Pilzgifte)</li>
                <li>Insektizide (Insektengifte)</li>
                <li>
                  weitere Chemikalien, die zum Schutz der Gesundheit von
                  Menschen und von Produkten eingesetzt werden – etwa
                  Desinfektionsmittel oder Rattengifte
                </li>
              </ul>
              <p>
                <Link allowWordWrap href='828308737500'>
                  Hier erhalten Sie Antworten auf die wichtigsten Fragen zu
                  Pestiziden
                </Link>{' '}
              </p>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui container'>
            <div className='ui two column grid doubling stackable'>
              <div className='row'>
                <div className='column special'>
                  <div className='titleBox'>
                    <h4 className='graphicTitle'>Pestizidverkauf</h4>
                    <div className='graphicSub'>
                      In Tonnen verkaufter Pestizide
                    </div>
                  </div>
                  <div className='graphicBox'>
                    <div className='boxWrapper'>
                      <div className='iframeBox'>
                        <AutofitIFrame
                          src='//datawrapper.dwcdn.net/XEHiG/'
                          style={{ height: '100%' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='column special'>
                  <div className='titleBox'>
                    <h4 className='graphicTitle'>
                      Einsatz von konventionellen Pestiziden
                    </h4>
                    <div className='graphicSub'>
                      2018, Hochrechnung nach Gewicht
                    </div>
                  </div>
                  <div className='graphicBox donut'>
                    <AutofitIFrame src='//datawrapper.dwcdn.net/xkwVH/' />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <p>
                Die Menge der verwendeten Pestizide ist in den zehn Jahren seit
                2008 nur sehr leicht zurückgegangen. Ein Teil der Insektizide
                und Fungizide wurde aber durch biokonforme Varianten ersetzt. Es
                gibt keine Herbizide, die für die Bioproduktion zugelassen sind.
              </p>
              <p>
                Fungizide werden mit 271 Tonnen pro Jahr am meisten in Reben
                gespritzt – im Weinbau werden alle anwendbaren
                Präventivmassnahmen ergriffen, um die Verbreitung von
                Krankheiten und Schädlingen zu verhindern.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <h4 className='chapter'>
                <b>4</b>
                <br />
                Nutzung des Agrarlandes
              </h4>
              <p>
                Die Schweizer Karte zeigt, in welchen Regionen welche
                Produktionsart dominiert.
              </p>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <MapSelector></MapSelector>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <h4 className='chapter'>
                <b>5</b>
                <br />
                Finanzierung der Landwirtschaft
              </h4>
              <p>
                Der Bund unterstützt die Landwirtschaft jährlich mit
                durchschnittlich 3,5 Milliarden Franken. Direktzahlungen machen
                mit 2,8 Milliarden Franken den grössten Teil der Steuergelder
                aus. Betriebe, die Direktzahlungen erhalten, müssen eine Reihe
                von Anforderungen erfüllen – darunter der ökologische
                Leistungsnachweis, der einen Mindeststandard für umweltgerechte
                Landwirtschaft vorschreibt. Für verschiedene andere Massnahmen
                werden jährlich 700 Millionen Franken gezahlt, die nicht
                unbedingt direkt an Bauern gehen, darunter die «Verkäsung» von
                Milch, die Förderung von neuen Absatzmärkten oder die
                Vermarktung von Schweizer Fleisch.
              </p>
            </div>
          </div>
        </div>

        {browserCheck.isMobile ? (
          <>
            <div className='ui vertical very fitted segment'>
              <div className='ui container'>
                <div className=''>
                  <div className='titleBox noBorder'>
                    <h4 className='graphicTitle'>
                      So viele Direktzahlungen sind 2019 in die Schweizer
                      Landwirtschaft geflossen
                    </h4>
                    <div className='graphicSub'>
                      2019, insgesamt 2,8 Milliarden Franken
                    </div>
                  </div>
                  <div className='graphicBox donut'>
                    <AutofitIFrame src='//datawrapper.dwcdn.net/NOz0n/' />
                  </div>
                </div>
              </div>
            </div>

            <div className='ui vertical very fitted segment'>
              <div className='ui text container'>
                <div className='ta text'>
                  <p>
                    2019 setzten sich die Direktzahlungen wie folgt zusammen:
                  </p>
                  <ul>
                    <li>
                      <span className='versorgungssicherheit'>
                        1081 Mio. Versorgungssicherheitsbeiträge
                      </span>
                      : Damit soll die Produktionskapazität für den Fall von{' '}
                      <b>Versorgungsengpässen</b> aufrechterhalten werden. Die
                      Beiträge hängen von der landwirtschaftlichen Nutzfläche
                      ab.
                    </li>
                    <li>
                      528 Mio.{' '}
                      <span className='kulturlandschaft'>
                        Kulturlandschaftsbeiträge
                      </span>
                      : Das Geld soll sicherstellen, dass landwirtschaftlich
                      nutzbares oder ökologisch wertvolles Land{' '}
                      <b>frei bleibt</b> und Waldwuchs verhindert wird.{' '}
                    </li>
                    <li>
                      489 Mio.{' '}
                      <span className='produktionssysteme'>
                        Produktionssystembeiträge
                      </span>
                      : Bestimmte Produktionsarten werden gefördert, z. B. die{' '}
                      <b>tierfreundliche</b> und die <b>Bioproduktion</b>, der
                      Anbau von Getreide oder Raps ohne Einsatz von Fungiziden
                      oder Insektiziden.{' '}
                    </li>
                    <li>
                      418 Mio.{' '}
                      <span className='biodiversitaet'>
                        Biodiversitätsbeiträge
                      </span>
                      : Das Geld soll <b>Artenvielfalt</b> und Lebensräume
                      fördern. Die Biodiversitätsflächen müssen während acht
                      Jahren nach entsprechenden Anforderungen bewirtschaftet
                      werden: z. B. <b>ohne Dünger</b> und
                      <b>Pflanzenschutzmittel</b>.
                    </li>
                    <li>
                      146 Mio.{' '}
                      <span className='landschaftsqualitaet'>
                        Landschaftsqualitätsbeiträge
                      </span>
                      : Unterstützt werden Projekte der Kantone zur Erhaltung,
                      Förderung und Weiterentwicklung{' '}
                      <b>vielfältiger Kulturlandschaften</b>.
                    </li>
                    <li>
                      104 Mio.{' '}
                      <span className='uebergang'>Übergangsbeiträge</span>:
                      Entschädigung aufgrund der Systemumstellung sowie
                      Kürzungen und Rückzahlungen.
                    </li>
                    <li>
                      37 Mio.{' '}
                      <span className='ressourceneffizienz'>
                        Ressourceneffizienzbeiträge
                      </span>
                      : Die Beiträge sollen die Nutzung der natürlichen
                      Ressourcen und die <b>Effizienz</b> verbessern.
                    </li>
                  </ul>
                  <p>
                    Auf einzelne Produkte heruntergebrochen, setzen sich die
                    Direktzahlungen wie folgt zusammen:
                  </p>
                </div>
              </div>
            </div>
            <div className='ui vertical very fitted segment'>
              <div className='ui container'>
                <div className=''>
                  <div className='titleBox noBorder'>
                    <h4 className='graphicTitle'>
                      Wie sind die landwirtschaftlichen Produkte finanziert?
                    </h4>
                    <div className='graphicSub'>
                      <div>2018, in Millionen Franken</div>
                      <div style={{ marginTop: '0.5rem' }}>
                        <span className='bold'>Lesebeispiel:</span> Geflügel
                        wird mit 326 Millionen Franken zum grossen Teil von den
                        Konsumentinnen direkt (an der Ladenkasse) bezahlt. Nur
                        41 Millionen werden von Steuerzahlerinnen über
                        Subventionen (Direktzahlungen) finanziert.
                      </div>
                    </div>
                  </div>
                  <div className='graphicBox'>
                    <AutofitIFrame src='//datawrapper.dwcdn.net/yoSOS/' />
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className='ui vertical very fitted segment'>
              <div className='ui container'>
                <div className='ui two column grid doubling stackable'>
                  <div className='row'>
                    <div className='column special noSpaceBetween'>
                      <div className='titleBox'>
                        <h4 className='graphicTitle'>
                          So viele Direktzahlungen sind 2019 in die Schweizer
                          Landwirtschaft geflossen
                        </h4>
                        <div className='graphicSub'>
                          2019, insgesamt 2,8 Milliarden Franken
                        </div>
                      </div>
                      <div className='graphicBox donut'>
                        <AutofitIFrame src='//datawrapper.dwcdn.net/NOz0n/' />
                      </div>
                    </div>

                    <div className='column special noSpaceBetween'>
                      <div className='titleBox'>
                        <h4 className='graphicTitle'>
                          Wie sind die landwirtschaftlichen Produkte finanziert?
                        </h4>
                        <div className='graphicSub'>
                          <div>2018, in Millionen Franken</div>
                          <div style={{ marginTop: '0.5rem' }}>
                            <span className='bold'>Lesebeispiel:</span> Geflügel
                            wird mit 326 Millionen Franken zum grossen Teil von
                            den Konsumentinnen direkt (an der Ladenkasse)
                            bezahlt. Nur 41 Millionen werden von
                            Steuerzahlerinnen über Subventionen
                            (Direktzahlungen) finanziert.
                          </div>
                        </div>
                      </div>
                      <div className='graphicBox'>
                        <AutofitIFrame src='//datawrapper.dwcdn.net/yoSOS/' />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='ui vertical very fitted segment'>
              <div className='ui text container'>
                <div className='ta text'>
                  <p>
                    2019 setzten sich die Direktzahlungen wie folgt zusammen:
                  </p>
                  <ul>
                    <li>
                      <span className='versorgungssicherheit'>
                        1081 Mio. Versorgungssicherheitsbeiträge
                      </span>
                      : Damit soll die Produktionskapazität für den Fall von{' '}
                      <b>Versorgungsengpässen</b> aufrechterhalten werden. Die
                      Beiträge hängen von der landwirtschaftlichen Nutzfläche
                      ab.
                    </li>
                    <li>
                      528 Mio.{' '}
                      <span className='kulturlandschaft'>
                        Kulturlandschaftsbeiträge
                      </span>
                      : Das Geld soll sicherstellen, dass landwirtschaftlich
                      nutzbares oder ökologisch wertvolles Land{' '}
                      <b>frei bleibt</b> und Waldwuchs verhindert wird.{' '}
                    </li>
                    <li>
                      489 Mio.{' '}
                      <span className='produktionssysteme'>
                        Produktionssystembeiträge
                      </span>
                      : Bestimmte Produktionsarten werden gefördert, z. B. die{' '}
                      <b>tierfreundliche</b> und die <b>Bioproduktion</b>, der
                      Anbau von Getreide oder Raps ohne Einsatz von Fungiziden
                      oder Insektiziden.{' '}
                    </li>
                    <li>
                      418 Mio.{' '}
                      <span className='biodiversitaet'>
                        Biodiversitätsbeiträge
                      </span>
                      : Das Geld soll <b>Artenvielfalt</b> und Lebensräume
                      fördern. Die Biodiversitätsflächen müssen während acht
                      Jahren nach entsprechenden Anforderungen bewirtschaftet
                      werden: z. B. <b>ohne Dünger</b> und
                      <b>Pflanzenschutzmittel</b>.
                    </li>
                    <li>
                      146 Mio.{' '}
                      <span className='landschaftsqualitaet'>
                        Landschaftsqualitätsbeiträge
                      </span>
                      : Unterstützt werden Projekte der Kantone zur Erhaltung,
                      Förderung und Weiterentwicklung{' '}
                      <b>vielfältiger Kulturlandschaften</b>.
                    </li>
                    <li>
                      104 Mio.{' '}
                      <span className='uebergang'>Übergangsbeiträge</span>:
                      Entschädigung aufgrund der Systemumstellung sowie
                      Kürzungen und Rückzahlungen.
                    </li>
                    <li>
                      37 Mio.{' '}
                      <span className='ressourceneffizienz'>
                        Ressourceneffizienzbeiträge
                      </span>
                      : Die Beiträge sollen die Nutzung der natürlichen
                      Ressourcen und die <b>Effizienz</b> verbessern.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <h4 className='chapter'>
                <b>6</b>
                <br />
                Selbstversorgungsgrad
              </h4>
              <p>
                Ernährungssicherheit ist in der Schweizer Verfassung seit 2017
                verankert (
                <Link href='https://www.fedlex.admin.ch/eli/cc/1999/404/de#art_104_a'>
                  Artikel 104a
                </Link>
                ). Die Schweizer Landwirtschaft soll die Bevölkerung mit
                Nahrungsmitteln versorgen. Der Selbstversorgungsgrad wird
                definiert als Verhältnis der Inlandproduktion zum inländischen
                Gesamtverbrauch.
              </p>
              <p>
                2019 wurde der Schweizer Nahrungsmittelverbrauch zu 57 Prozent
                mit Produkten aus inländischer Erzeugung gedeckt – dieser Wert,
                nach Energiegehalt der Produkte berechnet, ist seit 20 Jahren
                relativ konstant. Der Netto-Selbstversorgungsgrad liegt etwas
                tiefer, weil das importierte Futter einberechnet werden muss.{' '}
              </p>
              <p>
                Die Schweiz produziert mehr Milch und Milchprodukte, als sie
                verbraucht (116 Prozent). Der Bedarf an Früchten wird von der
                Schweizer Landwirtschaft nur zu einem Viertel gedeckt, der
                Bedarf an Eiern und Gemüse zur Hälfte.
              </p>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className=''>
              <div className='titleBox noBorder'>
                <h4 className='graphicTitle'>Selbstversorgungsgrad</h4>
                <div className='graphicSub'>2019, in Prozent</div>
              </div>
              <div className='graphicBox'>
                <div className='boxWrapper'>
                  <div className='iframeBox'>
                    <AutofitIFrame
                      src='//datawrapper.dwcdn.net/euXqE/'
                      style={{ height: '100%' }}
                    />
                  </div>
                </div>
                {/* <div className='source'>
                Hinweis: Anteil der Tiere, die auf einem Betrieb leben bei dem
                ein Teil oder die Gesamtheit seiner landwirtschaftlichen
                Nutzfläche biozertifiziert ist. Vor 1990 sind die Bio-Anteile
                nicht erfasst.
              </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='ui vertical very fitted segment'>
        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <h4 className='chapter'>
                <b>7</b>
                <br />
                Die Entwicklung von Bio
              </h4>
              <p>
                Der{' '}
                <Link href='https://www.bfs.admin.ch/bfs/de/home/statistiken/land-forstwirtschaft/landwirtschaft.html'>
                  biologische Landbau
                </Link>{' '}
                nimmt zu. 7561 Höfe produzierten im vergangenen Jahr biologisch:
                Das entspricht 15,3 Prozent aller Betriebe und 17 Prozent der
                gesamten Landwirtschaftsfläche. Allerdings sind das Steigerungen
                auf sehr niedrigem Niveau: Die konventionelle Landwirtschaft
                macht immer noch mehr als 80 Prozent der Produktion aus, ihre
                überragende Vorherrschaft bleibt ungebrochen.
              </p>
              <p>
                In jüngster Zeit ist der Bioanteil bei Geflügel besonders stark
                gewachsen: von 5 Prozent 2005 auf 10 Prozent heute. Auch die
                Ackerfläche wird zunehmend biologisch bewirtschaftet: 1990 lag
                der Anteil bei 1 Prozent, im Jahr 2020 waren es knapp 10
                Prozent.
              </p>
            </div>
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className=''>
              <div className='titleBox noBorder'>
                <h4 className='graphicTitle'>Bioanteil</h4>
                <div className='graphicSub'>
                  <div>
                    Anteil der Tiere, die auf einem Biohof gehalten werden bzw.
                    Anteil der Bioackerflächen
                  </div>
                  <div style={{ marginTop: '0.5rem' }}>
                    <span className='bold'>Lesebeispiel:</span> Im Jahr 2020
                    wurden 14 Prozent aller Rinder auf einem Biohof gehalten
                    und 10 Prozent aller Ackerflächen wurden nach Biostandard
                    bewirtschaftet.
                  </div>
                </div>
              </div>
              <div className='graphicBox'>
                <div className='boxWrapper'>
                  <div className='iframeBox'>
                    <AutofitIFrame
                      src='https://datawrapper.dwcdn.net/JLY0a/'
                      style={{ height: '100%' }}
                    />
                  </div>
                </div>
                {/* <div className='source'>
                Hinweis: Anteil der Tiere, die auf einem Betrieb leben bei dem
                ein Teil oder die Gesamtheit seiner landwirtschaftlichen
                Nutzfläche biozertifiziert ist. Vor 1990 sind die Bio-Anteile
                nicht erfasst.
              </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <p>
              Der Ursprung des Labels Bio liegt in den Umweltskandalen Ende der
              1980er-Jahre. Damals wurde fast täglich über Hormonfleisch oder
              erhöhte Nitratwerte im Kopfsalat berichtet, über Lebensmittel
              voller Chemie. In den 90er-Jahren wurde{' '}
              <Link href='963228038348'>Bio massentauglich</Link>. 2020 wurden
              in der Schweiz 3,8 Milliarden Franken mit Bioprodukten umgesetzt.
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div className='ta text portrait'>
              <h4 className='chapter'>
                <b>8</b>
                <br />
                Politische Meilensteine
              </h4>
              <p>
                Mit der Einführung von Direktzahlungen 1992 begann eine neue Ära
                der Schweizer Agrarpolitik. In einem Zeitstrahl zeigen wir
                einige der Meilensteine in der Entwicklung dieser Politik bis
                heute – kommentiert von Urs Niggli, einem der besten Kenner der
                Materie in der Schweiz.
              </p>
              <p>
                <img
                  src={require('images/urs-niggli-hoch.jpg')}
                  className='portrait'
                />
                Urs Niggli (67) gilt in der Schweizer Biobewegung als Visionär
                und Vorkämpfer. Er war dreissig Jahre lang Chef des Fibl, des
                Forschungsinstituts für biologischen Landbau. Niggli ist einer
                der international bekanntesten Agrarwissenschaftler. Er ist
                Gründer und Leiter des Instituts agroecology.science.
              </p>
            </div>
          </div>
        </div>

        <div className='ui vertical segment timeline'>
          <div className='ui container timeline'>
            <Timeline></Timeline>
          </div>
        </div>
      </div>

      <Footer
        articleId={ARTICLE_ID}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie gefällt Ihnen die Gestaltung?'
        ]}
        credits={[
          ['Text und Recherche', 'Salome Müller, Hans Brandt, Patrick Meier'],
          ['Umsetzung und Grafiken', 'Patrick Vögeli']
        ]}
      />
    </div>
  )
}
